import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../axiosConfig';
import { renderAsync } from 'docx-preview';
import { Link, useLocation } from 'react-router-dom';

function EmployeeDetails() {
   const location = useLocation();
    const activePath = location.pathname;
  
    const [currentUser, setCurrentUser] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentImages, setDocumentImages] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLabel, setPreviewLabel] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [message, setMessage] = useState('');

  const docxContainerRef = useRef(null);

  const verificationStatus = localStorage.getItem('verification_status');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('selected_document_for_details');
      if (!saved) return;
      const parsed = JSON.parse(saved);
      setSelectedDocument(parsed);
    } catch {
      // ignore
    }
  }, []);

  // Keep salary snapshot always fresh: selected_document_for_details can become stale
  // after salary mass-update (DP World Panipat).
  useEffect(() => {
    const refreshSalaryFields = async () => {
      if (!selectedDocument?.id) return;

      try {
        const res = await axiosInstance.get(`/documents/${selectedDocument.id}/`);
        const doc = res.data;

        setSelectedDocument((prev) => ({
          ...(prev || {}),
          basic: doc.basic ?? prev?.basic,
          da: doc.da ?? prev?.da,
          hra: doc.hra ?? prev?.hra,
          bonus: doc.bonus ?? prev?.bonus,
          special_allowance: doc.special_allowance ?? prev?.special_allowance,
          other1: doc.other1 ?? prev?.other1,
          other2: doc.other2 ?? prev?.other2,
          gross_total: doc.gross_total ?? prev?.gross_total,
          take_home: doc.take_home ?? prev?.take_home,
          ctc: doc.ctc ?? prev?.ctc,
        }));

        // Also update immediately visible table even if numbers arrive as strings
        // (prevents rounding/display confusion)
        // No-op for state; UI reads from selectedDocument.*

      } catch (e) {
        console.error('Failed to refresh salary snapshot:', e);
      }
    };

    refreshSalaryFields();
  }, [selectedDocument?.id]);


  // Fetch images for this employee/document
  useEffect(() => {
    if (!selectedDocument?.id) return;

    let cancelled = false;
    axiosInstance
      .get(`/get-image/${selectedDocument.id}/`)
      .then((response) => {
        if (cancelled) return;
        setDocumentImages(response.data || []);
      })
      .catch((e) => {
        console.error('Error fetching document images:', e);
        if (!cancelled) setDocumentImages([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDocument?.id]);

  useEffect(() => {
    const loadDocx = async () => {
      if (!previewFile) return;
      if (!/\.(docx?)$/i.test(previewFile)) return;
      if (!docxContainerRef.current) return;

      const container = docxContainerRef.current;
      container.innerHTML = '';

      let secureUrl = previewFile;
      if (secureUrl.startsWith('http://')) secureUrl = secureUrl.replace('http://', 'https://');

      try {
        const response = await fetch(secureUrl);
        const blob = await response.blob();
        await renderAsync(blob, container);
      } catch (error) {
        console.error('Error rendering DOCX:', error);
      }
    };

    loadDocx();
  }, [previewFile]);

  const handleFileClick = (file, label) => {
    let fileURL;

    if (file instanceof File || file instanceof Blob) {
      fileURL = URL.createObjectURL(file);
    } else if (typeof file === 'string') {
      const secureFile = file.replace(/^http:\/\//i, 'https://');
      fileURL = secureFile.startsWith('http')
        ? secureFile
        : `${process.env.REACT_APP_API_BASE_URL}${secureFile}`;
    } else {
      console.error('Unsupported file type');
      return;
    }

    if (/\.pdf(\?.*)?$/i.test(fileURL)) {
      window.open(fileURL, '_blank');
    } else {
      setPreviewFile(fileURL);
      setPreviewLabel(label);
    }
  };

  const handleDownloadPDF = async () => {
    const selectedDocumentId = selectedDocument?.id;
    if (!selectedDocumentId) return;

    setDownloadingPdf(true);
    try {
      const response = await axiosInstance.get(`/documents/download/${selectedDocumentId}/`, {
        responseType: 'blob',
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleOfferLetterDownload = async (documentId) => {
    try {
      setDownloadingPdf(true);

      const response = await axiosInstance.get(`/documents/${documentId}/offer-letter/`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Offer_Letter_${documentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading offer letter:', error);
      alert('Failed to download Offer Letter. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleAppointmentLetterDownload = async (documentId) => {
    try {
      setDownloadingPdf(true);

      const response = await axiosInstance.get(`/appointment-letter/${documentId}/`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Appointment_Letter_${documentId}.pdf`;
      link.click();
    } catch (err) {
      console.error('Error downloading appointment letter', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDPWorldAppointmentLetterDownload = async (documentId) => {
    try {
      setDownloadingPdf(true);

      const response = await axiosInstance.get(`/download-hindi-appointment/${documentId}/`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Hindi_Appointment_Letter_${documentId}.pdf`;
      link.click();
    } catch (err) {
      console.error('Error downloading Hindi Appointment Letter', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCriminalRecordDownload = async (documentId) => {
    setDownloadingPdf(true);

    try {
      const res = await axiosInstance.get(`download-criminal/${documentId}/`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Criminal_Record_With_Intro.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download Criminal Record PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };

const handleDownloadImages = async (documentId) => {
    if (!documentId) return;

    try {
      const response = await axiosInstance.get(
        `/download-document-images/${documentId}/`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `document_images_${documentId}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document images zip:', error);
      if (error?.response?.data) {
        const errorMessages = Object.keys(error.response.data)
          .map((key) => {
            const v = error.response.data[key];
            return Array.isArray(v) ? v.join(', ') : String(v);
          })
          .join(', ');
        setMessage(errorMessages);
      } else {
        setMessage('An error occurred. Please try again.');
      }
    }
  };

  const handleDownloadSingleImage = (imageId) => {
    const url = `/document-images/download-by-id/${imageId}/`;

    axiosInstance
      .get(url, { responseType: 'blob' })
      .then((response) => {
        const contentType = response.headers['content-type'];
        let fileExtension = 'jpg';

        if (contentType === 'application/pdf') fileExtension = 'pdf';
        else if (contentType === 'application/msword') fileExtension = 'doc';
        else if (contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') fileExtension = 'docx';
        else if (contentType === 'image/png') fileExtension = 'png';
        else if (contentType === 'image/webp') fileExtension = 'webp';
        else if (contentType === 'image/jpeg') fileExtension = 'jpg';

        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', `document_${imageId}.${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setPreviewFile(null);
        setPreviewImage(null);
      })
      .catch(() => {
        alert('An error occurred while downloading.');
      });
  };

  const renderFilePreview = (fileUrl, label, imageId) => {
    if (!fileUrl) return null;

    const secureFileUrl = fileUrl.startsWith('http')
      ? fileUrl
      : `${process.env.REACT_APP_API_BASE_URL}${fileUrl}`;

    const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(fileUrl);
    const isPDF = /\.pdf(\?.*)?$/i.test(fileUrl);
    const isDoc = /\.(doc|docx)$/i.test(fileUrl);

    const handleClick = () => {
      handleFileClick(fileUrl, label);
      setPreviewImage({ id: imageId });
    };

    return (
      <div style={{ textAlign: 'center', margin: '5px' }}>
        {isImage ? (
          <img
            src={secureFileUrl}
            alt={label}
            style={{ width: '60px', height: '60px', cursor: 'pointer' }}
            onClick={handleClick}
          />
        ) : isPDF ? (
          <div style={{ fontSize: '40px', color: 'red', cursor: 'pointer', fontWeight: 'bold' }} onClick={handleClick}>
            <i className="fas fa-file-pdf"></i>
          </div>
        ) : isDoc ? (
          <div style={{ fontSize: '40px', color: 'blue', cursor: 'pointer', fontWeight: 'bold' }} onClick={handleClick}>
            <i className="fas fa-file-word"></i>
          </div>
        ) : (
          <div style={{ fontSize: '16px', color: 'gray', cursor: 'pointer', fontWeight: 'bold' }} onClick={handleClick}>
            <i className="fas fa-file-alt"></i>
            <div style={{ fontSize: '12px', color: '#666' }}>Unknown File</div>
          </div>
        )}
      </div>
    );
  };

  const groupedImages = useMemo(() => {
    const base = (documentImages || []).reduce((acc, image) => {
      const raw = image?.document_type_info || '';
      const parts = raw.split(' - ');
      const category = parts[0];
      const name = parts.slice(1).join(' - ');

      const parsedCategory = (category || '').trim() || 'Others';
      const parsedName = (name || '').trim() || 'Document';

      // DEBUG: helps verify what backend sends for Criminal Record
      if (process.env.NODE_ENV !== 'production' && typeof raw === 'string' && raw.toLowerCase().includes('criminal')) {
        // eslint-disable-next-line no-console
        console.log('[EmployeeDetails] Criminal debug:', {
          raw,
          parsedCategory,
          parsedName,
          imageId: image?.id,
          fileName: image?.file,
        });
      }

      const formattedImage = { ...image, parsedCategory, parsedName };
      if (!acc[parsedCategory]) acc[parsedCategory] = [];
      acc[parsedCategory].push(formattedImage);
      return acc;
    }, {});


    // Ensure Joining exists
    if (!base['Joining']) base['Joining'] = [];

    const joining = base['Joining'];

    if (!joining.some((img) => img.parsedName === 'Offer Letter')) {
      joining.push({ id: 'offer-letter-pdf', parsedCategory: 'Joining', parsedName: 'Offer Letter', isOfferLetter: true });
    }

    if (!joining.some((img) => img.parsedName === 'Appointment Letter')) {
      joining.push({ id: 'appointment-letter-pdf', parsedCategory: 'Joining', parsedName: 'Appointment Letter', isAppointmentLetter: true });
    }

    if (verificationStatus === 'verified') {
      if (!base['Compliance']) base['Compliance'] = [];
      if (!base['Compliance'].some((img) => img.parsedName === 'Kyc Verification' && img.id === 'bgv-report-pdf')) {
        base['Compliance'].push({
          id: 'bgv-report-pdf',
          parsedCategory: 'Compliance',
          parsedName: 'Kyc Verification',
          isDownloadable: true,
        });
      }
    }

    return base;
  }, [documentImages, verificationStatus]);

  if (!selectedDocument) {
    return <div style={{ padding: 20 }}>No employee selected.</div>;
  }

  return (
      <div className="dashboard-wrapper">
          <div className="dashboard-sidebar">
            <div className="sidebar-header">
              <img src="/logo.png" alt="Logo" className="sidebar-logo" />
            </div>
    
            <nav className="sidebar-nav">
              <Link
                to="/admin-dashboard"
                className={`sidebar-link ${activePath === '/admin-dashboard' ? 'active' : ''}`}
              >
                <i className="fas fa-home"></i> Dashboard
              </Link>
    
              {currentUser?.role_name === 'admin' && (
                <Link to="/users" className={`sidebar-link ${activePath === '/users' ? 'active' : ''}`}>
                  <i className="fas fa-users"></i> Users
                </Link>
              )}
    
              <Link
                to="/departments"
                className={`sidebar-link ${activePath === '/departments' ? 'active' : ''}`}
              >
                <i className="fas fa-file-alt"></i> Departments
              </Link>
            </nav>
          </div>
    
          <div
            className="dashboard-main"
            style={{
              display: 'flex',
              gap: '20px',
              flexWrap: 'nowrap',
              alignItems: 'flex-start',
              padding: '20px',
            }}
          >
            <div style={{ flex: 3 }}>
              <div className="doc">
                <h1>Employee Details</h1>
              </div>

              <hr />

              <div className="document-info-grid" style={{ marginTop: 10 }}>


        <div className="grid-doc">
          <p>
            <strong>Employee Code:</strong> {selectedDocument.employee_code}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Contact No.:</strong> {selectedDocument.contact_number}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Email ID:</strong> {selectedDocument.email}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Name of Employee:</strong> {selectedDocument.first_name} {selectedDocument.middle_name} {selectedDocument.last_name}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Father's Name:</strong> {selectedDocument.father_first_name} {selectedDocument.father_middle_name} {selectedDocument.father_last_name}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Date Of Birth:</strong> {selectedDocument.date_of_birth}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Gender:</strong> {selectedDocument.gender}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Department:</strong> {selectedDocument.department}
          </p>
        </div>
        <div className="grid-doc">
          <p>
            <strong>Branch:</strong> {selectedDocument.branch}
          </p>
        </div>
      </div>

      <hr />

      {/* Salary snapshot (read-only) */}
      {/* <div className="form-fieldset" style={{ marginTop: 0 }}>
        <legend style={{ textDecoration: 'underline' }}>Salary</legend>
        <table className="salary-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Particulars</th>
              <th>Monthly (₹)</th>
              <th>Annual (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>BASIC</strong></td>
              <td>{Number(selectedDocument.basic || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.basic || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>DA</strong></td>
              <td>{Number(selectedDocument.da || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.da || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>HRA</strong></td>
              <td>{Number(selectedDocument.hra || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.hra || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>Bonus</strong></td>
              <td>{Number(selectedDocument.bonus || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.bonus || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>Special Allowance</strong></td>
              <td>{Number(selectedDocument.special_allowance || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.special_allowance || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>Other 1</strong></td>
              <td>{Number(selectedDocument.other1 || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.other1 || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>Other 2</strong></td>
              <td>{Number(selectedDocument.other2 || 0).toFixed(0)}</td>
              <td>{(Number(selectedDocument.other2 || 0) * 12).toFixed(0)}</td>
            </tr>
            <tr>
              <td><strong>Gross Total</strong></td>
              <td><strong>{Number(selectedDocument.gross_total || 0).toFixed(0)}</strong></td>
              <td><strong>{(Number(selectedDocument.gross_total || 0) * 12).toFixed(0)}</strong></td>
            </tr>
            <tr>
              <td><strong>Take Home</strong></td>
              <td><strong>{Number(selectedDocument.take_home || 0).toFixed(0)}</strong></td>
              <td><strong>{(Number(selectedDocument.take_home || 0) * 12).toFixed(0)}</strong></td>
            </tr>
            <tr>
              <td><strong>CTC</strong></td>
              <td><strong>{Number(selectedDocument.ctc || 0).toFixed(0)}</strong></td>
              <td><strong>{(Number(selectedDocument.ctc || 0) * 12).toFixed(0)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div> */}

      <hr />

      {(() => {
        const order = ['Joining', 'KYC', 'Compliance'];

        const categories = Object.keys(groupedImages || {}).sort((a, b) => {
          const ai = order.indexOf(a);
          const bi = order.indexOf(b);
          const aRank = ai === -1 ? 99 : ai;
          const bRank = bi === -1 ? 99 : bi;
          return aRank - bRank;
        });

        return categories.map((category) => {
          const images = groupedImages[category] || [];

          return (
            <fieldset key={category} className="form-fieldset">
              <legend style={{ textDecoration: 'underline' }}>{category}</legend>
              <div className="form-row-inline">
                {images.map((image, index) => (
                  <div key={index} className="form-inline-item">
                    <label style={{ fontSize: '12px' }}>{image.parsedName}:</label>

                    {image.isOfferLetter ? (
                      <span
                        onClick={() => !downloadingPdf && handleOfferLetterDownload(selectedDocument.id)}
                        style={{
                          cursor: downloadingPdf ? 'default' : 'pointer',
                          color: downloadingPdf ? '#999' : '#000',
                          border: `1px solid ${downloadingPdf ? '#999' : '#000'}`,
                          padding: '4px',
                          borderRadius: '4px',
                          textDecoration: 'underline',
                          fontSize: '12px',
                        }}
                      >
                        {downloadingPdf ? 'Downloading...' : 'Offer_Letter.pdf'}
                      </span>
                    ) : image.isAppointmentLetter ? (
                      <span
                        onClick={() => {
                          if (downloadingPdf) return;
                          const hindiBranches = ['DP World Panipat', 'DICT', 'Greenline', 'FORACE'];
                          if (hindiBranches.includes(selectedDocument.branch)) {
                            handleDPWorldAppointmentLetterDownload(selectedDocument.id);
                          } else {
                            handleAppointmentLetterDownload(selectedDocument.id);
                          }
                        }}
                        style={{
                          cursor: downloadingPdf ? 'default' : 'pointer',
                          color: downloadingPdf ? '#999' : '#000',
                          border: `1px solid ${downloadingPdf ? '#999' : '#000'}`,
                          padding: '4px',
                          borderRadius: '4px',
                          textDecoration: 'underline',
                          fontSize: '12px',
                        }}
                      >
                        {downloadingPdf
                          ? 'Downloading...'
                          : ['DP World Panipat', 'DICT', 'Greenline', 'FORACE'].includes(selectedDocument.branch)
                            ? 'Hindi_Appointment_Letter.pdf'
                            : 'Appointment_Letter.pdf'}
                      </span>
                    ) : category === 'Compliance' && image.isDownloadable ? (
                      <span
                        onClick={() => !downloadingPdf && handleDownloadPDF()}
                        style={{
                          cursor: downloadingPdf ? 'default' : 'pointer',
                          color: downloadingPdf ? '#999' : '#000',
                          padding: '4px',
                          borderRadius: '4px',
                          textDecoration: 'underline',
                          border: '1px solid #000',
                          fontSize: '12px',
                        }}
                      >
                        {downloadingPdf ? 'Downloading...' : 'BGV-Report.pdf'}
                      </span>
                    ) : image.parsedName === 'Criminal Record' ? (
                      <span
                        onClick={() => !downloadingPdf && handleCriminalRecordDownload(image.id)}
                        style={{
                          cursor: downloadingPdf ? 'default' : 'pointer',
                          color: downloadingPdf ? '#999' : '#000',
                          border: `1px solid ${downloadingPdf ? '#999' : '#000'}`,
                          padding: '4px',
                          borderRadius: '4px',
                          textDecoration: 'underline',
                          fontSize: '12px',
                        }}
                      >
                        {downloadingPdf ? 'Downloading...' : 'Criminal_Record_With_Intro.pdf'}
                      </span>
                    ) : (
                      <div
                        onClick={() => {
                          handleFileClick(image.file, image.parsedName);
                          setPreviewImage(image);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {renderFilePreview(image.file, 'File', image.id)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          );
        });
      })()}

      <button
        type="button"
        onClick={() => handleDownloadImages(selectedDocument.id)}
        style={{
          cursor: 'pointer',
          fontSize: '15px',
          color: '#fff',
          backgroundColor: '#000',
          borderRadius: '6px',
          padding: '5px 10px',
          fontWeight: 'bold',
          marginTop: 12,
        }}
      >
        Download All
      </button>

      {previewFile && (
        <div className="modal-overlay" onClick={() => setPreviewFile(null)}>
          <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <span className="close-icon" onClick={() => setPreviewFile(null)}>
                ×
              </span>
            </div>
            <h4>{previewLabel}</h4>

            {/(\\.pdf)$/i.test(previewFile) ? (
              <iframe src={previewFile} width="100%" height="500px" title="PDF Viewer" frameBorder="0" />
            ) : /\.(docx?)$/i.test(previewFile) ? (
              <div
                ref={docxContainerRef}
                style={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #ccc', padding: '10px' }}
              />
            ) : (
              <img src={previewFile} alt={previewLabel} style={{ maxWidth: '100%', maxHeight: '400px' }} />
            )}

            {previewImage?.id && (
              <button
                className="download-button"
                onClick={() => handleDownloadSingleImage(previewImage.id)}
              >
                Download
              </button>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
  );
}

export default EmployeeDetails;

